import React from 'react';

import { PAGE_BRIDGE, PILL_REACH_Y_PX, Root, type PinnedBarPlacement } from './PinnedBar.styles';

export type { PinnedBarPlacement } from './PinnedBar.styles';

/** How far the pill reaches above and below the bar by default (the Stats header's), for a consumer that places its own
 *  frame against it. */
export const PINNED_BAR_PILL_REACH_PX = PILL_REACH_Y_PX;
/** How far down its scroll area the bar pins in a dialog: the room of its pill's reach above it. */
export const PINNED_BAR_DIALOG_PIN_PX = PILL_REACH_Y_PX;

export type PinnedBarProps = {
  /** What the bar holds; a function of whether it is pinned right now, for content that changes as it pins. */
  children: React.ReactNode | ((stuck: boolean) => React.ReactNode);
  /** Where it pins: under the layout top bar on the page (the default), or at the top of the scroll area it sits in,
   *  in a dialog. */
  placement?: PinnedBarPlacement;
  /** The fill bridging the band above the pinned bar (a colour token): the page's background by default, a dialog's
   *  own surface in one. */
  bridge?: string;
  /** Called as the bar pins and unpins. */
  onStuckChange?: (stuck: boolean) => void;
  className?: string;
};

/** The nearest ancestor that scrolls, which a sticky element pins within; none when the document scrolls. */
const scrollAreaOf = (node: HTMLElement): HTMLElement | null => {
  for (let el = node.parentElement; el && el !== document.body; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
  }
  return null;
};

/**
 * A bar that pins under the layout top bar as the page scrolls (or at the top of a dialog's scroll area) and, once
 * pinned, lifts into a floating pill: the Stats page's pinned section header, for any content. It knows by itself when
 * it is pinned, measured from the top of the area it scrolls in, and says so (`data-stuck`, `onStuckChange`, and
 * `children` as a function). What else is pinned above it, and how far the pill reaches, are CSS custom properties a
 * consumer sets (see PinnedBar.styles).
 */
export const PinnedBar = React.forwardRef<HTMLDivElement, PinnedBarProps>(
  ({ children, placement = 'page', bridge = PAGE_BRIDGE, onStuckChange, className }, forwardedRef) => {
    const ref = React.useRef<HTMLDivElement | null>(null);
    const [stuck, setStuck] = React.useState(false);
    // Read by the effect without re-subscribing it on every render.
    const onChange = React.useRef(onStuckChange);
    onChange.current = onStuckChange;

    const setRef = React.useCallback(
      (el: HTMLDivElement | null): void => {
        ref.current = el;
        if (typeof forwardedRef === 'function') forwardedRef(el);
        else if (forwardedRef) forwardedRef.current = el;
      },
      [forwardedRef],
    );

    // Syncs `stuck` with the scroll: CSS cannot say whether a sticky element is stuck. The bar is held when its top is
    // at its pin, measured from the top of the area it scrolls in (the screen when the document scrolls). Checked once
    // per frame at most, on any scroll (captured, so a scroll area's own counts too) and on a resize.
    React.useEffect(() => {
      const el = ref.current;
      if (!el) return undefined;
      let frame = 0;
      let last: boolean | null = null;
      const check = (): void => {
        frame = 0;
        const pin = parseFloat(getComputedStyle(el).top);
        const area = scrollAreaOf(el);
        const origin = area ? area.getBoundingClientRect().top + area.clientTop : 0;
        // At its pin, not merely above it: a bar its box keeps from coming down that far (sitting higher at rest, or
        // carried off as its box scrolls away) is not held.
        const held = Number.isFinite(pin) && Math.abs(el.getBoundingClientRect().top - (origin + pin)) <= 1;
        if (held === last) return;
        last = held;
        setStuck(held);
        onChange.current?.(held);
      };
      const schedule = (): void => {
        if (frame === 0) frame = window.requestAnimationFrame(check);
      };
      // The first reading is no change to report: it only says how the bar starts.
      last = false;
      check();
      window.addEventListener('scroll', schedule, { capture: true, passive: true });
      window.addEventListener('resize', schedule);
      return () => {
        window.removeEventListener('scroll', schedule, { capture: true });
        window.removeEventListener('resize', schedule);
        if (frame !== 0) window.cancelAnimationFrame(frame);
      };
    }, []);

    return (
      <Root
        ref={setRef}
        className={className}
        data-pinned-bar=""
        data-stuck={stuck}
        $placement={placement}
        $bridge={bridge}
        $stuck={stuck}
      >
        {typeof children === 'function' ? children(stuck) : children}
      </Root>
    );
  },
);

PinnedBar.displayName = 'PinnedBar';

export default PinnedBar;
